import { useCallback, useEffect, useMemo } from "react";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { useTableDetailSidebarNavigationEvents } from "./useTableDetailSidebarNavigationEvents";
import { useToggleDetailSidebar } from "./useToggleDetailSidebar";

interface UseTableWithSidebarOptions<T> {
  tableId: string;
  items: T[];
  itemId: (item: T) => string;
  sidebarType: DetailSidebarType;
}

interface UseTableWithSidebarReturn<T> {
  onRowClick: (item: T, e: React.MouseEvent<HTMLDivElement>) => void;
  isRowSelected: (item: T) => boolean;
  sidebarSelectedRowId: string | null;
}

/**
 * Hook for integrating tables with the sidebar system.
 *
 * @param options Configuration for the table-sidebar integration
 * @returns Functions and state for handling row selection and sidebar display
 */
export function useTableWithSidebar<T>(
  args: UseTableWithSidebarOptions<T>,
): UseTableWithSidebarReturn<T> {
  const context = useDetailSidebarContext();
  const { toggleSidebar } = useToggleDetailSidebar();

  const { subscribe } = useTableDetailSidebarNavigationEvents();

  // Get the active table ID for navigation (prioritize root navigation context)
  const activeNavigationTableId =
    context.rootNavigationContext?.tableId || context.tableId;

  // Get the active object ID for navigation (prioritize root navigation context)
  const activeNavigationObjectId =
    context.rootNavigationContext?.objectId || context.objectId;

  // Navigation logic - direct event handlers
  // Use the root navigation object ID to find the current position in THIS table's items
  const currentIndex = useMemo(() => {
    return args.items.findIndex(
      (item) => args.itemId(item) === activeNavigationObjectId,
    );
  }, [args, activeNavigationObjectId]);
  const getNextItem = useCallback(
    (currentIndex: number) => {
      return args.items[currentIndex + 1];
    },
    [args.items],
  );
  const getPreviousItem = useCallback(
    (currentIndex: number) => {
      return args.items[currentIndex - 1];
    },
    [args.items],
  );
  const navigateToNextRow = useCallback(() => {
    if (currentIndex === -1) return;
    const nextItem = getNextItem(currentIndex);
    if (nextItem) {
      // Use the root navigation context's sidebarType and tableId to preserve original navigation scope
      context._toggleSidebar(
        undefined,
        args.itemId(nextItem),
        args.sidebarType,
        args.tableId,
        true, // This is internal navigation
      );
    }
  }, [args, context, currentIndex, getNextItem]);

  const navigateToPreviousRow = useCallback(() => {
    if (currentIndex === -1) return;
    const previousItem = getPreviousItem(currentIndex);
    if (previousItem) {
      // Use the root navigation context's sidebarType and tableId to preserve original navigation scope
      context._toggleSidebar(
        undefined,
        args.itemId(previousItem),
        args.sidebarType,
        args.tableId,
        true, // This is internal navigation
      );
    }
  }, [args, context, currentIndex, getPreviousItem]);
  // Handle row click
  const onRowClick = useCallback(
    (item: T, e: React.MouseEvent<HTMLDivElement>) => {
      const id = args.itemId(item);
      toggleSidebar(e, id, args.sidebarType, args.tableId);
    },
    [args, toggleSidebar],
  );

  // Subscribe to navigation events when this table is active
  useEffect(() => {
    const unsubscribeNext = subscribe("NAVIGATE_NEXT", () => {
      if (activeNavigationTableId !== args.tableId) return;
      navigateToNextRow();
    });
    const unsubscribePrevious = subscribe("NAVIGATE_PREVIOUS", () => {
      if (activeNavigationTableId !== args.tableId) return;
      navigateToPreviousRow();
    });

    return () => {
      unsubscribeNext();
      unsubscribePrevious();
    };
  }, [
    activeNavigationTableId,
    activeNavigationObjectId,
    args.tableId,
    navigateToNextRow,
    navigateToPreviousRow,
    subscribe,
    context.rootNavigationContext,
    context.objectId,
    currentIndex,
  ]);

  // Update navigation capabilities
  useEffect(() => {
    // Only update navigation capabilities if this table is the active navigation table
    if (activeNavigationTableId !== args.tableId) return;
    context.setCanNavigateNext(!!getNextItem(currentIndex));
    context.setCanNavigatePrevious(!!getPreviousItem(currentIndex));
  }, [
    args.tableId,
    args.items,
    args.itemId,
    activeNavigationTableId,
    activeNavigationObjectId,
    context.setCanNavigateNext,
    context.setCanNavigatePrevious,
    args,
    context,
    getNextItem,
    getPreviousItem,
    currentIndex,
  ]);

  // Check if a row is selected
  const isRowSelected = useCallback(
    (item: T): boolean => {
      // A row is selected if:
      // 1. This table is the active navigation table AND
      // 2. The item matches the root navigation object (the originally clicked row)
      const rootObjectId = context.rootNavigationContext?.objectId;
      return (
        activeNavigationTableId === args.tableId &&
        rootObjectId === args.itemId(item)
      );
    },
    [activeNavigationTableId, args, context.rootNavigationContext],
  );
  useDetailSidebarKeyboardNavigation({
    currentTableId: args.tableId,
  });

  return useMemo(
    () => ({
      onRowClick,
      isRowSelected,
      sidebarSelectedRowId:
        activeNavigationTableId === args.tableId
          ? context.rootNavigationContext?.objectId || context.objectId
          : null,
    }),
    [
      onRowClick,
      isRowSelected,
      context.objectId,
      context.rootNavigationContext,
      activeNavigationTableId,
      args.tableId,
    ],
  );
}

export function useDetailSidebarKeyboardNavigation(args: {
  currentTableId: string;
}): void {
  const { objectId, rootNavigationContext, closeSidebar } =
    useDetailSidebarContext();

  const { emit } = useTableDetailSidebarNavigationEvents();

  // Get the active navigation table ID (prioritize root navigation context)
  const activeNavigationTableId = rootNavigationContext?.tableId;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard navigation when sidebar is open,
      // and this table is the active navigation table
      if (
        !objectId ||
        !activeNavigationTableId ||
        !args.currentTableId ||
        args.currentTableId !== activeNavigationTableId
      )
        return;

      // Check if we're focused on an input, textarea, or contenteditable element
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.getAttribute("contenteditable") === "true"
      ) {
        return; // Don't interfere with form inputs
      }

      // Check if navigation provider is registered for this table
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          emit("NAVIGATE_PREVIOUS");
          break;
        case "ArrowDown":
          event.preventDefault();
          emit("NAVIGATE_NEXT");
          break;
        case "Escape":
          event.preventDefault();
          closeSidebar();
          break;
      }
    };

    // Always add event listener when sidebar is open
    if (objectId && activeNavigationTableId) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    objectId,
    closeSidebar,
    activeNavigationTableId,
    args.currentTableId,
    emit,
  ]);
}
